import { useMemo } from "react";

import type { ActorRef } from "types/actor";
import { useActorStoryStatus } from "./useActorStoryStatus";

// Re-export from the dep-free paths module so every existing caller of
// `useUserStoryStatus` can keep using the same barrel import.
export { buildUserStoriesPath } from "./paths";

function parsePositiveUserId(value: number | string | null | undefined): number | null {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

interface UseUserStoryStatusOptions {
  enabled?: boolean;
}

interface UseUserStoryStatusResult {
  /** True when the user has at least one active story right now. */
  data: boolean;
  isLoading: boolean;
}

/**
 * Backwards-compatible adapter for the previous `useUserStoryStatus(userId)`
 * API. Now delegates to the unified `useActorStoryStatus` so every "does
 * this user have a story?" check goes through the shared rail cache.
 *
 * Prefer `useActorStoryStatus(actor)` for new code.
 */
export function useUserStoryStatus(
  userId: number | string | null | undefined,
  options: UseUserStoryStatusOptions = {}
): UseUserStoryStatusResult {
  const normalizedUserId = parsePositiveUserId(userId);

  // Synthesize a minimal `Actor` so we can reuse the shared hook. The
  // username is a placeholder — `useActorStoryStatus` only consults
  // `id` + `kind` for the lookup.
  const actor: ActorRef = useMemo(() => {
    if (normalizedUserId === null) {
      return null;
    }
    return {
      id: normalizedUserId,
      kind: "user",
      username: "",
      displayName: null,
      avatarKey: null,
    };
  }, [normalizedUserId]);

  const { hasStory, isLoading } = useActorStoryStatus(
    options.enabled === false ? null : actor
  );

  return { data: hasStory, isLoading };
}
